/**
 * Frontend Ed25519 signature verification utility.
 *
 * Verifies Ed25519 signatures on credential data received from the server.
 * The server signs credential responses with its Ed25519 private key at issuance
 * and at response time. The frontend verifies using the Ed25519 public key
 * fetched from GET /api/credentialPublicKeys to confirm data authenticity and integrity.
 *
 * The public key is fetched once per browser session and cached in-memory.
 * Key rotation takes effect on the next session refresh without requiring a rebuild.
 *
 * Uses the Web Crypto API (Ed25519 algorithm) for signature verification.
 */

// Module-level cache — resolved once per browser session (shared with rsa-crypto.js
// if both are imported, but each module maintains its own cache independently)
let publicKeysPromise = null;

const getPublicKeys = () => {
  if (!publicKeysPromise) {
    //Detect the URL
    const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
    let url = '/api/credentialPublicKeys';

    if (rootUrl) {
      if (rootUrl.includes('localhost')) {
        url = 'http://localhost:4000/api/credentialPublicKeys';
      } else {
        url = `${rootUrl}/api/credentialPublicKeys`;
      }
    }

    publicKeysPromise = fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch credential public keys');
        return res.json();
      })
      .catch(err => {
        publicKeysPromise = null;
        throw err;
      });
  }
  return publicKeysPromise;
};

/**
 * Import the Ed25519 public key as a CryptoKey for verification.
 *
 * @param {string} publicKeyBase64 – base64-encoded 32-byte Ed25519 public key
 * @returns {Promise<CryptoKey>}
 */
async function importEd25519PublicKey(publicKeyBase64) {
  const rawBytes = Uint8Array.from(atob(publicKeyBase64), c => c.charCodeAt(0));

  // Wrap the 32-byte raw key in SubjectPublicKeyInfo DER format for Ed25519
  const spkiPrefix = new Uint8Array([
    0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00,
  ]);
  const spkiKey = new Uint8Array(spkiPrefix.length + rawBytes.length);
  spkiKey.set(spkiPrefix);
  spkiKey.set(rawBytes, spkiPrefix.length);

  return window.crypto.subtle.importKey(
    'spki',
    spkiKey,
    { name: 'Ed25519' },
    false,
    ['verify']
  );
}

/**
 * Verify an Ed25519 signature on a credential payload.
 *
 * The payload is JSON-serialized with deterministic key ordering (sorted keys)
 * before verification, matching the server's signing behavior.
 *
 * @param {Object} payload          – the credential data (without the ed25519Signature field)
 * @param {string} signature        – base64-encoded 64-byte Ed25519 signature
 * @param {string} [publicKeyBase64] – optional override for the public key
 * @returns {Promise<boolean>}      – true if the signature is valid
 */
export async function ed25519Verify(payload, signature, publicKeyBase64) {
  let keyBase64 = publicKeyBase64;
  if (!keyBase64) {
    try {
      const keys = await getPublicKeys();
      keyBase64 = keys.ed25519PublicKey;
    } catch (err) {
      // A check that cannot run has not passed.
      console.warn('[ed25519-crypto] Could not fetch Ed25519 public key; treating the signature as unverified.');
      return false;
    }
  }

  if (!keyBase64) {
    console.warn('[ed25519-crypto] Ed25519 public key not available; treating the signature as unverified.');
    return false;
  }

  try {
    const cryptoKey = await importEd25519PublicKey(keyBase64);

    // Canonicalize: JSON.stringify with sorted keys (matches server)
    const canonical = JSON.stringify(payload, Object.keys(payload).sort());
    const data = new TextEncoder().encode(canonical);

    const sigBytes = Uint8Array.from(atob(signature), c => c.charCodeAt(0));

    return await window.crypto.subtle.verify(
      { name: 'Ed25519' },
      cryptoKey,
      sigBytes,
      data
    );
  } catch (err) {
    console.error('[ed25519-crypto] Signature verification error:', err);
    return false;
  }
}

/**
 * Verify the Ed25519 signature on a credential response object.
 *
 * Expects the response to have an `ed25519Signature` field. Extracts the signature,
 * reconstructs the signed payload, and verifies.
 *
 * @param {Object} response – the decrypted credential response from the server
 * @returns {Promise<{data: Object, signatureValid: boolean}>}
 *   - data: the response without the ed25519Signature field
 *   - signatureValid: whether the signature verified successfully
 */
export async function verifyAndExtractSignature(response) {
  if (!response || !response.ed25519Signature) {
    // No signature present (legacy credential) — return data as-is
    return { data: response, signatureValid: null };
  }

  const { ed25519Signature, ...payload } = response;
  const signatureValid = await ed25519Verify(payload, ed25519Signature);

  if (!signatureValid) {
    console.error('[ed25519-crypto] CREDENTIAL SIGNATURE VERIFICATION FAILED — data may have been tampered with');
  }

  return { data: payload, signatureValid };
}
